/**
 * 認証済みユーザー操作 (e.g. POST /api/rooms) 向けの Turnstile token 取得。
 *
 * - Login.tsx の visible widget とは別に、 invisible + execution:'execute' モードの
 *   widget を body に hidden mount しておき、 `executeTurnstile()` で都度新規 token
 *   を取得する。
 * - Site key は既存の `VITE_TURNSTILE_SITE_KEY` を流用 (managed/visible でも
 *   `size: 'invisible'` + `execution: 'execute'` で programmatic 駆動できる)。
 *   未設定時は test key (always-pass) にフォールバック。
 * - dev / e2e の test secret は `1x0000000000000000000000000000000AA` (always pass)。
 *
 * 【プリフェッチ + ローテーション】
 *   `prefetchTurnstile()` を画面マウント時に呼ぶと、 背後で token を 1 個取得して
 *   キャッシュする。 `executeTurnstile()` はキャッシュがあれば即消費し、 消費直後に
 *   次の token を背後で取りに行く (= ボタン押下時のラグを実質ゼロに保つ)。
 *   token は single-use (worker 側で siteverify した瞬間に消費) かつ 5 分 TTL なので、
 *   消費 → 即補充 のローテーションが必要。
 */

const TURNSTILE_SITE_KEY: string =
  (import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined) ??
  '1x00000000000000000000AA';

/** Turnstile token の TTL は公称 300s。 margin を取って 270s で expire 扱い。 */
const TOKEN_TTL_MS = 270_000;

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          theme?: string;
          // 旧 'invisible' は 2024 後半に廃止。 プログラマブル駆動は
          //   execution: 'execute' + appearance: 'execute' で実現する。
          size?: 'normal' | 'compact' | 'flexible';
          execution?: 'render' | 'execute';
          appearance?: 'always' | 'execute' | 'interaction-only';
          callback?: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
        },
      ) => string;
      execute: (widgetIdOrContainer: string | HTMLElement) => void;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

let widgetId: string | null = null;
let widgetContainer: HTMLDivElement | null = null;
let pendingResolve: ((token: string) => void) | null = null;
let pendingReject: ((err: Error) => void) | null = null;

/**
 * turnstile スクリプトが window に乗るまで待つ。 CF 障害・広告ブロッカーで
 * スクリプトが永久にロードされないケースがあるため、 deadline を切って reject
 * する (= 無印だと呼び出し元の `await` が返らず、 部屋作成/再戦ボタンが永久
 * スピナーになる)。
 */
function waitForApi(timeoutMs = 10_000): Promise<void> {
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (typeof window !== 'undefined' && window.turnstile) {
        resolve();
        return;
      }
      if (Date.now() >= deadline) {
        reject(new Error('turnstile script load timeout'));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

function ensureWidget(): void {
  if (widgetId !== null || !window.turnstile) return;
  widgetContainer = document.createElement('div');
  // 通常は appearance:'execute' により Cloudflare 側がレンダリングを抑制するので
  // 視覚的には何も出ない。 万一 interactive challenge が要求された場合に備え、
  // 画面右下に小さく出現させる (= 完全 offscreen にすると ユーザーが触れず詰む)。
  widgetContainer.style.position = 'fixed';
  widgetContainer.style.bottom = '12px';
  widgetContainer.style.right = '12px';
  widgetContainer.style.zIndex = '9999';
  // 既定は非表示。 challenge 実行中 (= execute 中) だけ showWidget() で見せる。
  // これをしないと managed モードの widget (「Verify you are human」) が
  // 常時表示され、 SPA 遷移後も body に残って全画面で出っぱなしになる。
  // visibility (display ではなく) で隠すことで iframe を破棄せず再利用できる。
  widgetContainer.style.visibility = 'hidden';
  widgetContainer.style.pointerEvents = 'none';
  document.body.appendChild(widgetContainer);
  widgetId = window.turnstile.render(widgetContainer, {
    sitekey: TURNSTILE_SITE_KEY,
    // execution:'execute'        = `turnstile.execute()` 呼び出し時に初めて challenge を走らせる。
    // appearance:'interaction-only' = managed が silent に解ける場合は UI を一切出さない (成功
    //                                 チェックも表示しない)。 ユーザー操作が必要な場合のみ widget
    //                                 が出現する。 'execute' は成功時にも緑チェックが残るので不採用。
    execution: 'execute',
    appearance: 'interaction-only',
    callback: (token) => {
      const r = pendingResolve;
      pendingResolve = null;
      pendingReject = null;
      r?.(token);
    },
    'error-callback': () => {
      const j = pendingReject;
      pendingResolve = null;
      pendingReject = null;
      j?.(new Error('turnstile error'));
    },
    'expired-callback': () => {
      const j = pendingReject;
      pendingResolve = null;
      pendingReject = null;
      j?.(new Error('turnstile expired'));
    },
  });
}

/** challenge 実行中だけ widget を可視化する (managed モードで対話が要る場合に
 *  ユーザーが操作できるように)。 settle 後は hideWidget() で必ず隠す。 */
function showWidget(): void {
  if (widgetContainer) {
    widgetContainer.style.visibility = 'visible';
    widgetContainer.style.pointerEvents = 'auto';
  }
}
function hideWidget(): void {
  if (widgetContainer) {
    widgetContainer.style.visibility = 'hidden';
    widgetContainer.style.pointerEvents = 'none';
  }
}

/** widget を 1 度叩いて token を取る生実装。 `executingChain` で並行 execute を直列化する。 */
let executingChain: Promise<string> | null = null;
function performExecute(): Promise<string> {
  const next = (executingChain ?? Promise.resolve('')).then(async () => {
    await waitForApi();
    ensureWidget();
    if (!window.turnstile || widgetId === null) {
      throw new Error('turnstile not ready');
    }
    // challenge を走らせる直前に可視化し、 settle したら (成功/失敗/timeout いずれも)
    // 必ず隠す。 これで「対話が必要なときだけ一時的に出る」 挙動になる。
    showWidget();
    return new Promise<string>((resolve, reject) => {
      pendingResolve = resolve;
      pendingReject = reject;
      try {
        // reset せずに execute を再度呼ぶと widget が保持している「使用済み」 token を
        // そのまま返し、 server の siteverify で timeout-or-duplicate になる。
        // 毎回 reset して新規 token を発行させる (初回など reset 不可でも execute は続行)。
        try {
          window.turnstile!.reset(widgetId!);
        } catch {
          /* まだ token が無い状態での reset は無視 */
        }
        window.turnstile!.execute(widgetId!);
      } catch (e) {
        pendingResolve = null;
        pendingReject = null;
        reject(e instanceof Error ? e : new Error(String(e)));
      }
      // 念のためタイムアウト (15s)
      setTimeout(() => {
        if (pendingReject === reject) {
          pendingResolve = null;
          pendingReject = null;
          reject(new Error('turnstile timeout'));
        }
      }, 15_000);
    }).finally(() => hideWidget());
  });
  executingChain = next.catch(() => '');
  return next;
}

let cachedTokenPromise: Promise<string> | null = null;
let cachedTokenIssuedAt = 0;

/**
 * 背後で token を 1 個取得してキャッシュする (fire-and-forget)。
 * 既にフレッシュなキャッシュがあれば no-op。 失敗時は静かにキャッシュをクリアする
 * (次に `executeTurnstile()` が呼ばれた瞬間に lazy execute で復旧する)。
 *
 * 画面マウント時 (Home など、 後で `executeTurnstile()` を呼びそうな画面) で
 * 1 度呼んでおく想定。
 */
export function prefetchTurnstile(): void {
  if (cachedTokenPromise && Date.now() - cachedTokenIssuedAt < TOKEN_TTL_MS) return;
  cachedTokenIssuedAt = Date.now();
  cachedTokenPromise = performExecute();
  cachedTokenPromise.catch(() => {
    cachedTokenPromise = null;
    cachedTokenIssuedAt = 0;
  });
}

/**
 * 新しい Turnstile token を 1 つ取得する (single-use)。
 * - キャッシュがフレッシュなら即消費 + 背後で次の token をプリフェッチ (ローテーション)。
 * - キャッシュが無ければ lazy execute で取りに行く (= 初回ボタン押下のフォールバック)。
 */
export async function executeTurnstile(): Promise<string> {
  if (cachedTokenPromise && Date.now() - cachedTokenIssuedAt < TOKEN_TTL_MS) {
    const p = cachedTokenPromise;
    cachedTokenPromise = null;
    cachedTokenIssuedAt = 0;
    // 消費した瞬間に次の token を背後で取りに行く。
    prefetchTurnstile();
    return await p;
  }
  return await performExecute();
}

/**
 * 「Turnstile gate のかかった public endpoint」 を呼ぶ用のヘルパ。
 *
 * - 既存 sid Cookie 持ち (ログイン or 既発行ゲスト) は token 不要なので、 まず無印で 1 発投げる
 * - 未認証で 403 を食らったら `executeTurnstile()` で token を取って 1 度だけリトライ
 *
 * 現状の Worker は「token 必要時のみ 403 で body=`turnstile required: ...`」 を返すので、
 * 403 のみを retry trigger に。 他の 403 (= 認可エラー等) は即 return。
 */
export async function fetchWithTurnstileRetry(
  input: RequestInfo | URL,
  init: RequestInit = {},
): Promise<Response> {
  const res = await fetch(input, init);
  if (res.status !== 403) return res;
  const body = await res.clone().text().catch(() => '');
  if (!body.startsWith('turnstile')) return res;
  const token = await executeTurnstile();
  const headers = new Headers(init.headers);
  headers.set('cf-turnstile-response', token);
  return await fetch(input, { ...init, headers });
}
